/**
 * StorageManager - Handles localStorage/sessionStorage persistence
 */
export default class StorageManager {
    constructor(config = {}) {
        this.prefix = config.prefix || 'accessibility_';
        this.storageType = config.type || 'localStorage';
        this.remember = config.remember !== false;
        this.storage = this._getStorage();
    }

    /**
     * Get storage engine (localStorage or sessionStorage)
     */
    _getStorage() {
        try {
            const storage = window[this.storageType];
            const testKey = this.prefix + '__test';
            storage.setItem(testKey, '1');
            storage.removeItem(testKey);
            return storage;
        } catch (e) {
            console.warn('[A11Y] Storage not available, using memory fallback');
            return this._createMemoryStorage();
        }
    }

    /**
     * Memory-based fallback when localStorage is unavailable
     */
    _createMemoryStorage() {
        const data = {};
        return {
            getItem: (key) => data[key] || null,
            setItem: (key, value) => { data[key] = String(value); },
            removeItem: (key) => { delete data[key]; },
            clear: () => { Object.keys(data).forEach(k => delete data[k]); }
        };
    }

    /**
     * Get a value from storage
     */
    get(key, defaultValue = null) {
        try {
            const raw = this.storage.getItem(this.prefix + key);
            if (raw === null) return defaultValue;
            return JSON.parse(raw);
        } catch (e) {
            return defaultValue;
        }
    }

    /**
     * Set a value in storage
     */
    set(key, value) {
        if (!this.remember) return;
        try {
            this.storage.setItem(this.prefix + key, JSON.stringify(value));
        } catch (e) {
            console.warn('[A11Y] Storage write failed:', e.message);
        }
    }

    /**
     * Remove a value from storage
     */
    remove(key) {
        try {
            this.storage.removeItem(this.prefix + key);
        } catch (e) {
            // Silently fail
        }
    }

    /**
     * Check if a key exists in storage
     */
    has(key) {
        return this.storage.getItem(this.prefix + key) !== null;
    }

    /**
     * Clear all widget-related storage entries
     */
    clearAll() {
        try {
            const keysToRemove = [];
            for (let i = 0; i < this.storage.length; i++) {
                const key = this.storage.key(i);
                if (key && key.startsWith(this.prefix)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => this.storage.removeItem(key));
        } catch (e) {
            // Fallback: try clear on memory storage
            if (this.storage.clear) {
                this.storage.clear();
            }
        }
    }

    /**
     * Get all stored feature states
     */
    getAllFeatureStates() {
        const states = {};
        try {
            for (let i = 0; i < this.storage.length; i++) {
                const key = this.storage.key(i);
                if (key && key.startsWith(this.prefix + 'feature_')) {
                    const featureKey = key.replace(this.prefix + 'feature_', '');
                    states[featureKey] = JSON.parse(this.storage.getItem(key));
                }
            }
        } catch (e) {
            // Return empty states
        }
        return states;
    }
}
